/**
 * TanStack Query hooks for the site's theme (fork + template/asset editing).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { themesApi, accountThemesApi } from '../api/themes';
import { sitesApi } from '../api/sites';
import type {
  CreateAccountThemeRequest,
  DuplicateAccountThemeRequest,
  FileEncoding,
  ForkThemeRequest,
} from '../api/themes';

/** Save-file mutation body. `encoding: 'base64'` writes a binary asset. */
export interface SaveThemeFileVars {
  path: string;
  content: string;
  encoding?: FileEncoding;
  expectedContent: string;
}

// ============================================================================
// Query Keys
// ============================================================================

export const themeKeys = {
  all: (siteSlug: string) => ['sites', siteSlug, 'theme'] as const,
  file: (siteSlug: string, path: string) => ['sites', siteSlug, 'theme', 'file', path] as const,
  pageTemplates: (siteSlug: string) => ['sites', siteSlug, 'theme', 'page-templates'] as const,
};

/** AB#644: stock (bundled) theme files — read-only, and the same for everyone. */
export const bundledThemeKeys = {
  file: (id: string, path: string) => ['themes', 'bundled', id, 'file', path] as const,
};

export const accountThemeKeys = {
  /** Prefix of every key below — invalidating it refreshes lists AND files. */
  all: ['account', 'themes'] as const,
  list: (site?: string) => ['account', 'themes', 'list', site ?? ''] as const,
  file: (id: number, path: string) => ['account', 'themes', 'file', id, path] as const,
};

// ============================================================================
// Hooks
// ============================================================================

/** Fetch the site's active theme + file list */
export function useTheme(siteSlug: string) {
  return useQuery({
    queryKey: themeKeys.all(siteSlug),
    queryFn: () => themesApi.get(siteSlug),
    enabled: !!siteSlug,
  });
}

/** AB#945: page templates the site's theme offers (under themeKeys.all, so file writes refresh it). */
export function usePageTemplates(siteSlug: string) {
  return useQuery({
    queryKey: themeKeys.pageTemplates(siteSlug),
    queryFn: () => themesApi.pageTemplates(siteSlug),
    enabled: !!siteSlug,
  });
}

/** Fetch a single theme file's content */
export function useThemeFile(siteSlug: string, path: string | null) {
  return useQuery({
    queryKey: themeKeys.file(siteSlug, path ?? ''),
    queryFn: () => themesApi.getFile(siteSlug, path!),
    enabled: !!siteSlug && !!path,
  });
}

/**
 * Read one file of a BUNDLED theme (AB#644). Stock themes never change between
 * requests, so this is cached indefinitely; there is no write twin.
 */
export function useBundledThemeFile(themeId: string | null, path: string | null) {
  return useQuery({
    queryKey: bundledThemeKeys.file(themeId ?? '', path ?? ''),
    queryFn: () => sitesApi.bundledThemeFile(themeId!, path!),
    enabled: !!themeId && !!path,
    staleTime: Infinity,
  });
}

/** Fork a bundled theme — or copy a blueprint — into a site-owned copy */
export function useForkTheme(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ForkThemeRequest) => themesApi.fork(siteSlug, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: themeKeys.all(siteSlug) });
    },
  });
}

/** Save a theme file. Rejects with ApiError (422 = Tera parse error). */
export function useSaveThemeFile(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ path, content, encoding, expectedContent }: SaveThemeFileVars) =>
      themesApi.putFile(siteSlug, path, content, encoding, expectedContent),
    onSuccess: () => {
      // themeKeys.all is a prefix of themeKeys.file, so this refreshes both.
      queryClient.invalidateQueries({ queryKey: themeKeys.all(siteSlug) });
    },
  });
}

/** Delete a theme file. Rejects with ApiError (422 = delete breaks the parse). */
export function useDeleteThemeFile(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (path: string) => themesApi.deleteFile(siteSlug, path),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: themeKeys.all(siteSlug) });
    },
  });
}

/** Discard the custom theme and revert to the bundled one */
export function useDiscardTheme(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => themesApi.discard(siteSlug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: themeKeys.all(siteSlug) });
    },
  });
}

// ============================================================================
// Account themes (AB#616)
// ============================================================================

/**
 * List the saved themes (newest first). Pass a site slug to include the
 * `account`-scoped themes of that site's account (AB#650).
 */
export function useAccountThemes(siteSlug?: string) {
  return useQuery({
    queryKey: accountThemeKeys.list(siteSlug),
    queryFn: () => accountThemesApi.list(siteSlug),
  });
}

/** Create an account theme from a bundled theme or a site's current theme. */
export function useCreateAccountTheme() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAccountThemeRequest) => accountThemesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountThemeKeys.all });
    },
  });
}

/** Duplicate an account theme (optionally into the other scope). */
export function useDuplicateAccountTheme() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }: DuplicateAccountThemeRequest & { id: number }) =>
      accountThemesApi.duplicate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountThemeKeys.all });
    },
  });
}

/** Rename an account theme. */
export function useRenameAccountTheme() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      accountThemesApi.rename(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountThemeKeys.all });
    },
  });
}

/** Delete an account theme (no cascade — sites fall back to their bundled theme). */
export function useDeleteAccountTheme() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => accountThemesApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountThemeKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Account theme files — same contract as the site theme-file hooks above.
// ---------------------------------------------------------------------------

/** Read one file of an account theme. */
export function useAccountThemeFile(id: number | null, path: string | null) {
  return useQuery({
    queryKey: accountThemeKeys.file(id ?? 0, path ?? ''),
    queryFn: () => accountThemesApi.getFile(id!, path!),
    enabled: id != null && !!path,
  });
}

/** Save one file. Rejects with ApiError (422 = Tera parse error). */
export function useSaveAccountThemeFile(id: number | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ path, content, encoding, expectedContent }: SaveThemeFileVars) =>
      accountThemesApi.putFile(id!, path, content, encoding, expectedContent),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountThemeKeys.all });
    },
  });
}

/** Delete one file. Rejects with ApiError (422 = delete breaks the parse). */
export function useDeleteAccountThemeFile(id: number | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (path: string) => accountThemesApi.deleteFile(id!, path),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountThemeKeys.all });
    },
  });
}
